import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cake, Clock, Truck, CreditCard, Phone, MapPin, ChevronRight, PartyPopper, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Target date: October 2, 2026 - Birthday Cake Sale
const TARGET_DATE = new Date("2026-10-02T00:00:00");

const HomePage = () => {
  const [cakes, setCakes] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const fetchCakes = async () => {
      try {
        const response = await axios.get(`${API}/cakes`);
        setCakes(response.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching cakes:", error);
      }
    };
    fetchCakes();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = TARGET_DATE - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Cake className="w-10 h-10 text-primary" />
            <span className="font-heading text-3xl font-semibold text-foreground tracking-tight">Sis Carol</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/rentals">
              <Button variant="outline" className="rounded-full px-4 py-2">
                🎉 Party Rentals
              </Button>
            </Link>
            <Link to="/order">
              <Button data-testid="nav-order-btn" className="rounded-full bg-primary text-primary-foreground px-6 py-2 hover:opacity-90 transition-opacity">
                Order Now
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 relative overflow-hidden">
        {/* Confetti/Fetti Burst Animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Floating balloons */}
          <div className="absolute top-20 left-10 text-6xl animate-bounce" style={{animationDuration: '3s'}}>🎈</div>
          <div className="absolute top-32 right-20 text-5xl animate-bounce" style={{animationDuration: '2.5s', animationDelay: '0.5s'}}>🎈</div>
          <div className="absolute top-40 left-1/4 text-4xl animate-bounce" style={{animationDuration: '2.8s', animationDelay: '0.3s'}}>🎈</div>
          <div className="absolute top-24 right-1/3 text-5xl animate-bounce" style={{animationDuration: '3.2s', animationDelay: '0.7s'}}>🎈</div>
          
          {/* Confetti pieces */}
          <div className="absolute top-16 left-20 text-3xl animate-spin" style={{animationDuration: '4s'}}>🎊</div>
          <div className="absolute top-28 right-32 text-2xl animate-spin" style={{animationDuration: '3s'}}>🎊</div>
          <div className="absolute top-48 left-16 text-2xl animate-pulse">✨</div>
          <div className="absolute top-36 right-16 text-3xl animate-pulse" style={{animationDelay: '0.5s'}}>✨</div>
          <div className="absolute top-52 left-1/3 text-2xl animate-pulse" style={{animationDelay: '1s'}}>✨</div>
          <div className="absolute top-44 right-1/4 text-3xl animate-pulse" style={{animationDelay: '0.3s'}}>⭐</div>
          
          {/* Party poppers */}
          <div className="absolute top-20 left-1/2 text-4xl animate-bounce" style={{animationDuration: '2s'}}>🎉</div>
          <div className="absolute top-60 right-10 text-3xl animate-bounce" style={{animationDuration: '2.2s', animationDelay: '0.4s'}}>🎉</div>
        </div>

        {/* Happy Birthday Banner */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-2xl p-4 md:p-6 text-center shadow-2xl animate-pulse relative overflow-hidden">
            <div className="absolute inset-0 bg-white/10 animate-shimmer"></div>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <span className="text-4xl md:text-5xl">🎂</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
                Happy Birthday Sis Carol!
              </h2>
              <span className="text-4xl md:text-5xl">🎂</span>
            </div>
            <p className="text-white/90 text-lg md:text-xl mt-2 font-medium">🎉 Celebrating with Sweet Deals! 🎉</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 shadow-sm">
                <Phone className="w-5 h-5 text-primary" />
                <span className="font-medium text-primary">1876-277-2648</span>
              </div>
              <p className="font-accent text-2xl text-primary">Freshly Baked with Love</p>
              <h1 className="font-heading text-5xl md:text-7xl leading-[0.9] tracking-tight text-foreground">
                Celebrate with <span className="text-primary">Sis Carol's</span> Cakes
              </h1>
              <p className="text-lg text-muted-foreground max-w-md">
                Handcrafted cakes for your special moments. Made with the finest ingredients and a whole lot of love.
              </p>
              
              {/* Countdown */}
              <div data-testid="countdown-section" className="bg-secondary/50 rounded-3xl p-6 md:p-8">
                <p className="font-accent text-xl text-primary mb-4">🎂 Birthday Cake Sale Countdown!</p>
                <p className="text-sm text-muted-foreground mb-6">Special prices on October 2nd - Don't miss out!</p>
                <div className="flex gap-6 md:gap-10">
                  <div className="text-center">
                    <span data-testid="countdown-days" className="countdown-digit">{countdown.days}</span>
                    <p className="countdown-label mt-1">Days</p>
                  </div>
                  <div className="text-center">
                    <span data-testid="countdown-hours" className="countdown-digit">{countdown.hours}</span>
                    <p className="countdown-label mt-1">Hours</p>
                  </div>
                  <div className="text-center">
                    <span data-testid="countdown-minutes" className="countdown-digit">{countdown.minutes}</span>
                    <p className="countdown-label mt-1">Mins</p>
                  </div>
                  <div className="text-center">
                    <span data-testid="countdown-seconds" className="countdown-digit">{countdown.seconds}</span>
                    <p className="countdown-label mt-1">Secs</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/order">
                  <Button data-testid="hero-order-btn" className="rounded-full bg-primary text-primary-foreground px-8 py-6 text-lg font-medium hover:-translate-y-1 hover:shadow-lg transition-all">
                    Order Your Cake
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#cakes">
                  <Button variant="outline" className="rounded-full border-border px-8 py-6 text-lg font-medium hover:bg-secondary transition-colors">
                    View Menu
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Image - Birthday Cake Sale Flyer */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_4d378b5e-9ef7-4ec3-b362-94c616ea2fd2/artifacts/wzaze58h_screenwriter_temp_1789432727895%20%281%29.jpg" 
                  alt="Sis Carol Birthday Cake Sale - October 2nd 2026" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Birthday Cake Sale Banner */}
      <section className="py-8 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/20 rounded-full animate-pulse"></div>
          <div className="absolute top-1/2 right-10 w-16 h-16 bg-white/20 rounded-full animate-bounce"></div>
          <div className="absolute bottom-2 left-1/4 w-12 h-12 bg-white/20 rounded-full animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce">
                <PartyPopper className="w-8 h-8 md:w-10 md:h-10 text-pink-500" />
              </div>
              <div className="text-white">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎂</span>
                  <span className="text-sm md:text-base font-medium uppercase tracking-wider">October 2nd Special!</span>
                </div>
                <h3 className="font-heading text-2xl md:text-4xl font-bold">Birthday Cake Sale</h3>
                <p className="text-white/90 text-sm md:text-base">Celebrate with sweet deals on all cakes!</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-3 text-white text-center">
                <div className="flex items-center gap-2 justify-center">
                  <Gift className="w-5 h-5" />
                  <span className="font-medium">Special Prices</span>
                </div>
                <p className="text-sm text-white/80">One Day Only!</p>
              </div>
              
              <Link to="/order">
                <Button 
                  className="bg-white text-pink-600 hover:bg-pink-50 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid="birthday-sale-btn"
                >
                  Order Now
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="p-6 rounded-2xl bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
              <Cake className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">Fresh Baked</h3>
              <p className="text-sm text-muted-foreground">Made fresh for every order with premium ingredients</p>
            </Card>
            <Card className="p-6 rounded-2xl bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
              <Truck className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">Delivery</h3>
              <p className="text-sm text-muted-foreground">Safe delivery to your doorstep</p>
            </Card>
            <Card className="p-6 rounded-2xl bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
              <CreditCard className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">Easy Payment</h3>
              <p className="text-sm text-muted-foreground">Bank transfer or cash on delivery</p>
            </Card>
            <Card className="p-6 rounded-2xl bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
              <Clock className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">On Time</h3>
              <p className="text-sm text-muted-foreground">Timely delivery for your special day</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Cakes Preview Section */}
      <section id="cakes" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-accent text-2xl text-primary mb-4">Our Selection</p>
            <h2 className="font-heading text-4xl md:text-5xl leading-tight text-foreground">Featured Cakes</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {cakes.map((cake, index) => (
              <Card 
                key={cake.id} 
                data-testid={`cake-card-${index}`}
                className="cake-card group relative overflow-hidden rounded-3xl bg-white shadow-sm border border-border/50"
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={cake.image_url} 
                    alt={cake.name}
                    className="cake-image w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2">{cake.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{cake.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-2xl text-primary">J${cake.price.toLocaleString()}</span>
                    <Link to="/order">
                      <Button variant="outline" size="sm" className="rounded-full hover:bg-primary hover:text-white transition-colors">
                        Order
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/order">
              <Button className="rounded-full bg-primary text-primary-foreground px-8 py-4 hover:-translate-y-1 hover:shadow-lg transition-all">
                View All Cakes & Order
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-primary/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1672081880696-ee16349de67e?w=800" 
                alt="Baker decorating cake"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <p className="font-accent text-2xl text-primary">Our Story</p>
              <h2 className="font-heading text-4xl md:text-5xl leading-tight">Baked with Love Since Day One</h2>
              <p className="text-muted-foreground text-lg">
                At Sis Carol, every cake tells a story. We believe in creating not just desserts, but memories. 
                Each cake is handcrafted with passion, using only the finest ingredients to ensure your celebration 
                is nothing short of perfect.
              </p>
              <div className="flex items-center gap-4 pt-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Questions? Call us</p>
                  <p className="font-heading text-xl font-semibold">1876-277-2648</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Cake className="w-10 h-10 text-primary" />
                <span className="font-heading text-3xl font-semibold tracking-tight">Sis Carol</span>
              </div>
              <p className="text-white/70">
                Crafting delicious memories, one cake at a time.
              </p>
            </div>
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-white/70">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> 1876-277-2648
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" /> Irie Eatz Restaurant, Market Street, Ocho Rios
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Payment Methods</h4>
              <div className="space-y-2 text-white/70">
                <p>Bank Transfer</p>
                <p>Cash on Delivery</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/50">
            <p>&copy; 2025 Sis Carol. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Balloon - Party Rentals Promo */}
      <Link 
        to="/rentals"
        className="fixed bottom-6 left-6 z-50 group cursor-pointer"
        data-testid="floating-balloon"
      >
        <div className="relative animate-bounce" style={{ animationDuration: '2s' }}>
          {/* Balloon */}
          <div className="w-20 h-24 bg-gradient-to-b from-pink-400 to-pink-600 rounded-full relative shadow-lg group-hover:scale-110 transition-transform">
            {/* Balloon highlight */}
            <div className="absolute top-3 left-3 w-4 h-6 bg-white/30 rounded-full transform rotate-[-20deg]"></div>
            {/* Balloon knot */}
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[10px] border-l-transparent border-r-transparent border-t-pink-600"></div>
          </div>
          {/* String */}
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 w-[2px] h-10 bg-pink-300"></div>
          {/* Text bubble */}
          <div className="absolute -right-2 top-1/2 transform translate-x-full -translate-y-1/2 bg-white rounded-xl px-3 py-2 shadow-lg whitespace-nowrap">
            <p className="text-sm font-bold text-pink-600">We also do</p>
            <p className="text-sm font-bold text-purple-600">Party Rentals! 🎉</p>
            <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2 w-0 h-0 border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent border-r-white"></div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default HomePage;
